/**
 * Live-mode history bounds.
 *
 * Two different things feed a live page, and they have different bounds:
 *
 *  - The **live feed** is the columnar Arrow WebSocket. It delivers a snapshot of the server's recent
 *    ring on subscribe and then deltas, so it only ever covers the last couple of minutes
 *    ({@link LIVE_STREAM_BUFFER_MINUTES}). That is all a default live session holds.
 *  - **Backfill** is a separate HTTP read (`/api/stream/sources/:name/data/merged`) used when the user
 *    asks to see further back than the stream ring holds, or when a freshly-opened page would
 *    otherwise draw an empty chart. That endpoint is tiered server-side (see
 *    `server_stream/controllers/tieredRead.js`): the in-memory recentStore ring for recent data, the
 *    durable Parquet cache for anything older.
 *
 * So a window wider than the buffer is not refused — it triggers an explicit backfill for exactly
 * that depth. What is NOT allowed is an unbounded read: "Full" resolves to
 * {@link LIVE_FULL_WINDOW_MINUTES}, not to all of history. An unbounded live window re-read on every
 * tick is what locks the browser.
 *
 * Retention then tracks whatever the user is actually asking to see. Keeping more than that is pure
 * cost: derived line types are recomputed over the whole retained window on each materialize.
 */

/**
 * Rolling buffer kept from the live stream. Small on purpose — this is the steady-state cost of
 * simply watching a live page, and 0.5/1/2-minute windows are served entirely from it with no HTTP
 * read at all. Matches the server's default columnar ring retention (STREAM_COLUMNAR_RETENTION_MS).
 */
export const LIVE_STREAM_BUFFER_MINUTES = 2;

/**
 * What the "Full" (0) time-window setting resolves to in live mode. Matches the largest explicit
 * option on the time-window dial, so every window the user can pick has a defined, bounded depth.
 */
export const LIVE_FULL_WINDOW_MINUTES = 30;

/** Absolute ceiling on the live time window, however it was set (the signal is cross-window synced). */
export const LIVE_TIME_WINDOW_MAX_MINUTES = 30;

/**
 * Slack retained beyond the window itself so its trailing edge is never clipped mid-render — the
 * window slides continuously, and pruning exactly at the boundary would visibly truncate the series.
 */
export const LIVE_HISTORY_RETENTION_SLACK_MS = 60_000;

/**
 * The window a given `timeWindow()` setting actually means in live mode, in minutes.
 * `0` = "Full" resolves to {@link LIVE_FULL_WINDOW_MINUTES}; anything else is clamped to the ceiling.
 */
export function liveEffectiveWindowMinutes(windowMinutes: number): number {
  const requested = Number(windowMinutes);
  if (!Number.isFinite(requested) || requested <= 0) {
    return LIVE_FULL_WINDOW_MINUTES;
  }
  return Math.min(requested, LIVE_TIME_WINDOW_MAX_MINUTES);
}

/**
 * How deep to backfill for a given window setting, in minutes. Never less than the live buffer, so a
 * freshly-opened page always has a couple of minutes of data to draw.
 */
export function liveBackfillMinutesForWindow(windowMinutes: number): number {
  return Math.max(LIVE_STREAM_BUFFER_MINUTES, liveEffectiveWindowMinutes(windowMinutes));
}

/** How much history to retain in memory for a given window setting, in ms. */
export function liveRetentionMsForWindow(windowMinutes: number): number {
  return liveBackfillMinutesForWindow(windowMinutes) * 60_000 + LIVE_HISTORY_RETENTION_SLACK_MS;
}
